import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Calendar } from '@vendasta/meetings';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { ContactFormCacheService } from '../bookme-contact-form/contact-form-cache.service';
import { TimeSlot } from '../bookme/bookme';
import { trimTimeRangeToFromNow } from '../util';

@Component({
  selector: 'app-bookme-book',
  templateUrl: './bookme-book.component.html',
  styleUrls: ['./bookme-book.component.scss'],
  standalone: false,
})
export class BookmeBookComponent implements OnInit, OnDestroy {
  calendarSlug: string;
  meetingTypeSlug: string;
  calendar$: Observable<Calendar>;

  loadTimeSlotsError: Error;
  timeSlots$: Observable<TimeSlot[]>;
  timezoneOffset$: Observable<string>;
  private readonly subscriptions: Subscription[] = [];

  private loadTimeSlots$$: Subject<{
    timeSpan: TimeSlot;
    timezone: string;
    userId: string;
  }> = new Subject();
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public readonly bookmenowService: BookMeNowStoreService,
    private contactFormCacheService: ContactFormCacheService
  ) {}

  ngOnInit(): void {
    const currentPath = this.router.url;
    this.bookmenowService.storeInitialPath(currentPath);
    this.route.queryParams.subscribe((params) => {
      // To pre-fill booking form fetch data from query param in the url
      // Only set cache if form-specific params are present (not just any query param)
      const formParamKeys = [
        'firstName',
        'lastName',
        'email',
        'phoneNumber',
        'countryCode',
        'bookingSource',
      ];
      const hasFormParams = formParamKeys.some((key) => !!params[key]);
      if (hasFormParams) {
        this.contactFormCacheService.set({
          firstName: this.safeDecodeURIComponent(params.firstName) || '',
          lastName: this.safeDecodeURIComponent(params.lastName) || '',
          email: this.safeDecodeURIComponent(params.email) || '',
          phoneNumber: this.safeDecodeURIComponent(params.phoneNumber) || '',
          countryCode: this.safeDecodeURIComponent(params.countryCode) || '',
          comment: '',
          location: '',
          bookingSource:
            this.safeDecodeURIComponent(params.bookingSource) || '',
        });
        this.clearDataFromUrl();
      }
    });

    this.calendarSlug = this.route.snapshot.params.calendarId;
    this.meetingTypeSlug = this.route.snapshot.params.meetingTypeId;
    this.calendar$ = this.bookmenowService.calendar$;
    this.subscriptions.push(
      this.bookmenowService
        .loadMeetingTypeForGivenCalendarIdAndSlug(
          this.calendarSlug,
          this.meetingTypeSlug
        )
        .subscribe({
          error: (err) => this.bookmenowService.handleMeetingTypeError(err),
        })
    );
    this.timeSlots$ = this.loadTimeSlots$$.pipe(
      debounceTime(200),
      switchMap((req) => {
        return this.bookmenowService.loadAvailableTimeSlots({
          timeSpan: trimTimeRangeToFromNow(req.timeSpan),
          timeZone: req.timezone ? { id: req.timezone } : undefined,
          userId: req.userId ? req.userId : undefined,
        });
      }),
      tap(() => (this.loadTimeSlotsError = undefined)),
      catchError((err) => {
        console.error(err);
        this.loadTimeSlotsError = new Error(
          $localize`:@@BOOK.ERROR_OCCURRED:An error occurred.`
        );
        return of([]);
      })
    );
  }

  private handleMeetingTypeError(err: { error?: { message?: string } }): void {
    const message = (err?.error?.message || '').toLowerCase();

    if (message.includes('no longer exists')) {
      this.bookmenowService.setErrorDisplay('meeting-type-deleted');
    } else if (message.includes('not found')) {
      this.bookmenowService.setErrorDisplay('error-404');
    } else {
      this.bookmenowService.setErrorDisplay('error-meeting-type');
    }
  }

  // once fetch all data from url,
  // Set all the query param as 'null' to remove from it
  clearDataFromUrl(): void {
    this.router.navigate([], {
      queryParams: {
        firstName: null,
        lastName: null,
        email: null,
        phoneNumber: null,
        countryCode: null,
        comment: null,
        location: null,
        bookingSource: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  private safeDecodeURIComponent(value: string | undefined): string {
    try {
      return value ? decodeURIComponent(value) : '';
    } catch {
      return value || '';
    }
  }

  onTimeSlotSelected(timeSlot: TimeSlot): void {
    this.bookmenowService.setSelectedTimeSlot(timeSlot);
  }

  onConfirmChanges(): void {
    this.router.navigate(['contact-form'], {
      queryParamsHandling: 'preserve',
      relativeTo: this.route,
    });
  }

  onTimeRangeChanged(timeRange: { start: Date; end: Date }): void {
    this.bookmenowService.setSelectedTimeRange(timeRange);
    this.requestTimeSlots({ timeRange });
  }

  onHostChanged(userId: string | null): void {
    if (userId === 'any') {
      this.bookmenowService.setSelectedHost(null);
      return this.requestTimeSlots({});
    }
    this.bookmenowService.setSelectedHost(userId);
    this.requestTimeSlots({ userId });
  }

  onTimezoneChanged(timezone: string): void {
    this.bookmenowService.setTimezone(timezone, false);
    this.requestTimeSlots({ timezone });
  }

  ngOnDestroy(): void {
    (this.subscriptions || []).forEach((s) => s.unsubscribe());
  }

  private requestTimeSlots(req: {
    timeRange?: TimeSlot;
    timezone?: string;
    userId?: string;
  }): void {
    this.loadTimeSlots$$.next({
      timeSpan: trimTimeRangeToFromNow(req.timeRange),
      timezone: req.timezone,
      userId: req.userId,
    });
  }
}
